/**
 * Secure token storage utilities
 * Provides a fallback mechanism from httpOnly cookies to localStorage with security warnings
 */

interface StorageAdapter {
  getToken(): string | null
  setToken(token: string): void
  removeToken(): void
  isSecure(): boolean
}

class LocalStorageAdapter implements StorageAdapter {
  private readonly TOKEN_KEY = 'auth_token'
  private readonly HASH_KEY = 'auth_token_hash'
  private readonly TIMESTAMP_KEY = 'auth_token_timestamp'

  getToken(): string | null {
    try {
      const token = localStorage.getItem(this.TOKEN_KEY)
      const hash = localStorage.getItem(this.HASH_KEY)
      const timestamp = localStorage.getItem(this.TIMESTAMP_KEY)

      if (!token || !hash || !timestamp) {
        return null
      }

      // Verify token integrity
      if (this.simpleHash(token) !== hash) {
        console.warn('Token integrity check failed, removing token')
        this.removeToken()
        return null
      }

      // Check if token is expired
      if (this.isTokenExpired(parseInt(timestamp))) {
        console.warn('Token expired, removing token')
        this.removeToken()
        return null
      }

      return token
    } catch (error) {
      console.error('Failed to retrieve token:', error)
      return null
    }
  }

  setToken(token: string): void {
    try {
      const timestamp = Date.now()
      const hash = this.simpleHash(token)

      localStorage.setItem(this.TOKEN_KEY, token)
      localStorage.setItem(this.HASH_KEY, hash)
      localStorage.setItem(this.TIMESTAMP_KEY, timestamp.toString())

      // Log security warning
      console.warn('🔒 Security Warning: Token stored in localStorage. Consider implementing httpOnly cookies for production.')

    } catch (error) {
      console.error('Failed to store token:', error)
    }
  }

  removeToken(): void {
    localStorage.removeItem(this.TOKEN_KEY)
    localStorage.removeItem(this.HASH_KEY)
    localStorage.removeItem(this.TIMESTAMP_KEY)
  }

  isSecure(): boolean {
    return false // localStorage is not secure against XSS
  }

  private simpleHash(input: string): string {
    let hash = 0
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash
    }
    return hash.toString(36)
  }

  private isTokenExpired(timestamp: number): boolean {
    const tokenAge = Date.now() - timestamp
    const maxAge = 12 * 60 * 60 * 1000 // 12 hours
    return tokenAge > maxAge
  }
}

class SecureTokenStorage {
  private adapter: StorageAdapter

  constructor() {
    // Use localStorage for token-based authentication
    // Token-based auth is simpler and more reliable for SPAs
    this.adapter = new LocalStorageAdapter()
  }

  getToken(): string | null {
    return this.adapter.getToken()
  }

  setToken(token: string): void {
    if (!token) {
      throw new Error('Token cannot be empty')
    }

    // Basic token validation
    if (token.length < 10) {
      throw new Error('Token appears to be invalid')
    }

    this.adapter.setToken(token)
  }

  removeToken(): void {
    this.adapter.removeToken()
  }

  isSecure(): boolean {
    return this.adapter.isSecure()
  }

  getSecurityInfo(): { secure: boolean; storage: string; warnings: string[] } {
    const warnings: string[] = []
    
    if (!this.isSecure()) {
      warnings.push('Tokens are stored in localStorage, vulnerable to XSS attacks')
    }
    
    if (!this.isHttpsOnly()) {
      warnings.push('Application should be served over HTTPS only')
    }

    return {
      secure: this.isSecure(),
      storage: this.adapter.constructor.name,
      warnings
    }
  }

  private isHttpsOnly(): boolean {
    return window.location.protocol === 'https:' || window.location.hostname === 'localhost'
  }
}

// Export singleton instance
export const secureStorage = new SecureTokenStorage()

// Export security check function
export const performSecurityCheck = (): void => {
  const info = secureStorage.getSecurityInfo()
  
  if (info.warnings.length > 0) {
    console.group('🔒 Security Warnings')
    info.warnings.forEach(warning => console.warn(`⚠️ ${warning}`))
    console.groupEnd()
  }
}